import { HttpClient } from '@angular/common/http';
import { Injectable, Injector, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@app/environment';
import {
  BehaviorSubject,
  defer,
  Observable,
  of,
  ReplaySubject,
  Subject,
} from 'rxjs';
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  map,
  mergeMap,
  share,
  take,
  timeout,
} from 'rxjs/operators';

import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { NgxPermissionsService, ValidationFn } from 'ngx-permissions';
import { adminPermissions, appPermissions } from './auth.permissions';
import {
  idleTimeoutMinutes,
  notificationDuration,
  sessionEndWarningMinutes,
  sessionLifetimeMinutes,
  signOutRequestTimeoutMs,
} from '../config.vars';
import { NotificationService } from '../service/notification.service';
import {
  changePasswordErrorMessage,
  CognitoError,
  loginErrorMessage,
} from './auth-messages';

/** The outcome of a password change; `code` is Cognito's error name. */
export type ChangePasswordResult =
  { ok: true } | { ok: false; code: string; message: string };

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private permissionsService = inject(NgxPermissionsService);
  private notificationService = inject(NotificationService);
  // The Router is fetched when needed: injected here it would close the cycle
  // Router -> AuthGuard -> AuthService -> Router while the router starts.
  private injector = inject(Injector);
  private zone = inject(NgZone);
  private sessionTimers: ReturnType<typeof setTimeout>[] = [];

  private userPool = new CognitoUserPool(environment.userPoolData);
  private isAuthenticated = new ReplaySubject<boolean>(1);
  private userGroups = new BehaviorSubject<string[]>([]);
  private userName = new ReplaySubject<string | null>(1);
  private userId = new ReplaySubject<string | null>(1);
  private pwChangeAttributes: { [prop: string]: string } | null = null;
  private pwChangeUser: CognitoUser | null = null;

  public get needPasswordUpdate() {
    return this.pwChangeAttributes !== null;
  }

  private fetchFreshToken = new Subject<void>();
  private freshToken$: Observable<string | null> = this.fetchFreshToken.pipe(
    concatMap(() =>
      this.getCurrentSession().pipe(
        take(1),
        mergeMap((session) =>
          session && session.isValid()
            ? of(session)
            : this.refreshSession(session)
        ),
        catchError((err) => {
          // Cognito refuses an expired or revoked refresh token; anything else
          // (e.g. the network) leaves the session as it is.
          if (
            err?.name === 'NotAuthorizedException' ||
            err?.code === 'NotAuthorizedException'
          ) {
            this.endSession();
          } else {
            this.notificationService.notify(
              err?.message || 'Session Expired',
              'warning',
              notificationDuration.warning
            );
          }
          return of(null);
        })
      )
    ),
    map((session) => (session && session.getIdToken().getJwtToken()) || null),
    share()
  );

  get token$(): Observable<string | null> {
    return new Observable((subscriber) => {
      this.freshToken$.pipe(take(1)).subscribe((x) => {
        subscriber.next(x);
        subscriber.complete();
      });
      this.fetchFreshToken.next();
    });
  }

  get isAuthenticated$() {
    return this.isAuthenticated.pipe(distinctUntilChanged());
  }

  get username$() {
    return this.userName.pipe(distinctUntilChanged());
  }

  get userid$() {
    return this.userId.pipe(distinctUntilChanged());
  }

  get usergroups$() {
    return this.userGroups.pipe(distinctUntilChanged());
  }

  constructor() {
    this.permissionsService.loadPermissions(
      appPermissions,
      this.checkPermission as ValidationFn
    );
    this.getCurrentSession().subscribe({
      next: this.importSessionData,
      error: () => this.importSessionData(null),
    });
  }

  /**
   * Ends an expired session: forgets the tokens in the browser and opens the
   * login, which returns to the current page afterwards.
   */
  private endSession(
    message = `Your session has ended after ${sessionLifetimeMinutes} minutes. Please sign in again.`,
    wasSignedIn = this.userPool.getCurrentUser() !== null
  ) {
    this.userPool.getCurrentUser()?.signOut();
    this.importSessionData(null);
    if (!wasSignedIn) {
      return;
    }
    this.notificationService.notify(
      message,
      'warning',
      notificationDuration.warning
    );
    const router = this.injector.get(Router);
    const returnUrl = router.url;
    if (!returnUrl.startsWith('/login')) {
      router.navigate(['/login'], { queryParams: { returnUrl } });
    }
  }

  /** Warns before the session ends and ends it on time, counted from the sign-in. */
  private scheduleSessionEnd(session: CognitoUserSession | null) {
    this.sessionTimers.forEach((timer) => clearTimeout(timer));
    this.sessionTimers = [];
    const authTime = session?.getIdToken().payload['auth_time'];
    if (!session || !session.isValid() || typeof authTime !== 'number') {
      return;
    }
    const end = (authTime + sessionLifetimeMinutes * 60) * 1000;
    const warning = end - sessionEndWarningMinutes * 60 * 1000;
    const now = Date.now();
    this.zone.runOutsideAngular(() => {
      if (warning > now) {
        this.sessionTimers.push(
          setTimeout(
            () =>
              this.zone.run(() =>
                this.notificationService.notify(
                  `Your session ends in ${sessionEndWarningMinutes} minutes; you will then be asked to sign in again.`,
                  'warning',
                  notificationDuration.warning
                )
              ),
            warning - now
          )
        );
      }
      this.sessionTimers.push(
        setTimeout(
          () => this.zone.run(() => this.endSession()),
          Math.max(end - now, 0)
        )
      );
    });
  }

  private getCurrentSession(): Observable<CognitoUserSession | null> {
    return new Observable((subscriber) => {
      const user = this.userPool.getCurrentUser();
      if (user) {
        user.getSession(
          (err: Error | null, session: CognitoUserSession | null) => {
            if (err) {
              subscriber.error(err);
            } else if (session === null) {
              subscriber.next(null);
              subscriber.complete();
            } else {
              subscriber.next(session);
              subscriber.complete();
            }
          }
        );
      } else {
        subscriber.next(null);
        subscriber.complete();
      }
    });
  }

  private refreshSession(
    session: CognitoUserSession | null
  ): Observable<CognitoUserSession | null> {
    return new Observable((subscriber) => {
      const user = this.userPool.getCurrentUser();
      if (user && session) {
        user.refreshSession(
          session.getRefreshToken(),
          (err: Error | null, session: CognitoUserSession | null) => {
            if (err) {
              this.importSessionData(null);
              subscriber.error(err);
            } else {
              this.importSessionData(session);
              subscriber.next(session);
              subscriber.complete();
            }
          }
        );
      } else {
        this.isAuthenticated.next(false);
        subscriber.next(null);
        subscriber.complete();
      }
    });
  }

  private importSessionData = (session: CognitoUserSession | null) => {
    this.scheduleSessionEnd(session);
    this.isAuthenticated.next(!!session && session.isValid());
    this.userName.next(
      session
        ? session.getIdToken().payload['given_name'] +
            ' ' +
            session.getIdToken().payload['family_name']
        : null
    );
    this.userId.next(session?.getIdToken().payload['sub'] || null);
    this.userGroups.next(
      (session?.getIdToken().payload['cognito:groups'] || []) as string[]
    );
  };

  public checkPermission = (...permissions: string[]) => {
    return permissions.some(
      (permission: string) =>
        this.userGroups.value.some((userPermission) =>
          adminPermissions.includes(userPermission)
        ) || this.userGroups.value.includes(permission)
    );
  };

  /**
   * Changes the password of the signed-in user (Cognito ChangePassword, which
   * needs the current password). Answers Cognito's error for the form to
   * show instead of a notification.
   */
  changePassword(
    oldPassword: string,
    newPassword: string
  ): Observable<ChangePasswordResult> {
    return this.getCurrentSession().pipe(
      catchError(() => of(null)),
      mergeMap((session) => {
        const user = this.userPool.getCurrentUser();
        if (!user || !session || !session.isValid()) {
          return of<ChangePasswordResult>({
            ok: false,
            code: 'NoSession',
            message: 'Your session has ended. Please sign in again.',
          });
        }
        return new Observable<ChangePasswordResult>((observer) => {
          user.setSignInUserSession(session);
          user.changePassword(oldPassword, newPassword, (err) => {
            if (err) {
              const error = err as CognitoError;
              observer.next({
                ok: false,
                code: error.code || error.name || 'Error',
                message: changePasswordErrorMessage(error),
              });
            } else {
              this.notificationService.notify(
                'Your password was changed.',
                'success',
                notificationDuration.success
              );
              observer.next({ ok: true });
            }
            observer.complete();
          });
        });
      })
    );
  }

  handleNewPassword(newPassword: string) {
    return new Observable<boolean>((observer) => {
      if (this.pwChangeUser !== null) {
        this.pwChangeUser.completeNewPasswordChallenge(
          newPassword,
          this.pwChangeAttributes,
          {
            onSuccess: (session) => {
              this.importSessionData(session);
              this.notificationService.notify(
                'Password updated',
                'success',
                notificationDuration.success
              );
              this.pwChangeAttributes = null;
              this.pwChangeUser = null;
              observer.next(session && session.isValid());
              observer.complete();
            },
            onFailure: (err) => {
              this.notificationService.notify(
                err.message || 'Password update failed',
                'error',
                notificationDuration.error
              );
              this.isAuthenticated.next(false);
              this.userGroups.next([]);
              observer.next(false);
              observer.complete();
            },
          }
        );
      }
    });
  }

  /**
   * Signs the user out after the idle time (IdleTimeoutService): Cognito
   * GlobalSignOut as on logout, then the tokens in the browser are dropped
   * even if that failed, and the login opens and returns to the current page.
   */
  endSessionAfterInactivity(): Observable<void> {
    const wasSignedIn = this.userPool.getCurrentUser() !== null;
    return this.logout().pipe(
      catchError(() => of(false)),
      take(1),
      map(() =>
        this.endSession(
          `You were signed out after ${idleTimeoutMinutes} minutes without activity. Please sign in again.`,
          wasSignedIn
        )
      )
    );
  }

  /**
   * Tells the API that the user signs out (method signOut), so that it
   * refuses the ID tokens of this sign-in from now on, also tokens that were
   * copied before (2023 pen test, finding 06). The sign-out goes on if the
   * API cannot be reached.
   */
  private endSessionOnServer(): Observable<unknown> {
    return defer(() =>
      this.http.post(`${environment.apiBase}/signOut`, { method: 'signOut' })
    ).pipe(
      timeout(signOutRequestTimeoutMs),
      catchError(() => of(null))
    );
  }

  /**
   * Signs the user out: the API refuses the current tokens, Cognito revokes
   * the refresh tokens of every session of the user (GlobalSignOut), and the
   * tokens in the browser are dropped, also when GlobalSignOut fails. Emits
   * whether GlobalSignOut succeeded and never fails.
   */
  logout(): Observable<boolean> {
    return this.getCurrentSession().pipe(
      catchError(() => of(null)),
      mergeMap((session) => {
        const user = this.userPool.getCurrentUser();
        if (!session || !user) {
          user?.signOut();
          this.importSessionData(null);
          return of(false);
        }
        return this.endSessionOnServer().pipe(
          mergeMap(
            () =>
              new Observable<boolean>((subscriber) => {
                user.setSignInUserSession(session);
                user.globalSignOut({
                  onSuccess: () => {
                    this.importSessionData(null);
                    subscriber.next(true);
                    subscriber.complete();
                  },
                  onFailure: (err) => {
                    // The API already refuses the tokens; they must not stay
                    // in the browser either.
                    user.signOut();
                    this.importSessionData(null);
                    this.notificationService.notify(
                      `You were signed out in this browser, but Cognito could not end the session: ${err.message || 'unknown error'}`,
                      'warning',
                      notificationDuration.warning
                    );
                    subscriber.next(false);
                    subscriber.complete();
                  },
                });
              })
          )
        );
      })
    );
  }

  login(Username: string, Password: string) {
    return new Observable<boolean>((observer) => {
      const authenticationDetails = new AuthenticationDetails({
        Username,
        Password,
      });

      const user = new CognitoUser({
        Username,
        Pool: this.userPool,
      });

      user.authenticateUser(authenticationDetails, {
        onSuccess: (session) => {
          this.importSessionData(session);
          this.http
            .post(`${environment.apiBase}/sync`, { method: 'sync' })
            .subscribe();
          observer.next(session && session.isValid());
          observer.complete();
        },
        onFailure: (err) => {
          this.notificationService.notify(
            loginErrorMessage(err),
            'error',
            notificationDuration.error
          );
          this.isAuthenticated.next(false);
          this.userGroups.next([]);
          observer.next(false);
          observer.complete();
        },
        newPasswordRequired: (userAttributes, requiredAttributes: string[]) => {
          this.pwChangeAttributes =
            requiredAttributes instanceof Array
              ? requiredAttributes.reduce(
                  (acc, cur) => ({ ...acc, [cur]: userAttributes[cur] }),
                  {}
                )
              : {};
          this.pwChangeUser = user;
          observer.next(false);
          observer.complete();
        },
      });
    });
  }
}
